import { createApi } from '@reduxjs/toolkit/query/react';
import { PAGE_SIZE, axiosBaseQuery } from '../common';
import {
  ISelectionCreate,
  ISelectionItem,
  ISelectionList,
  ISelectionListRequest,
  ISelectionScope,
  RuleOperationType,
  IPatientItem,
  IMissingOrderProducts,
  ILabSearch,
  IOrderingProviderForOrder,
  IProductSearch,
  IBulkOrderBody,
} from './models';
import { getArrayCriterias } from './helpers';
import { createSlice, PayloadAction } from '@reduxjs/toolkit';

export const selectionsApiSlice = createApi({
  reducerPath: 'selections/api',
  baseQuery: axiosBaseQuery({
    baseUrl: 'selections',
  }),
  tagTypes: ['Selections', 'SelectionsRefetch'],
  endpoints: builder => ({
    getSelections: builder.query<IState<ISelectionItem>, ISelectionListRequest>({
      query: data => ({
        url: '/fn/search',
        method: 'POST',
        data,
      }),
      providesTags: ['Selections'],
      transformResponse: (response: IAxiosResponse<ISelectionList>): IState<ISelectionItem> => ({
        data: response.data.selectionItems,
        isEnd: response.data.selectionItems.length < PAGE_SIZE,
      }),
      serializeQueryArgs: ({ endpointName }) => {
        return endpointName;
      },
      merge(currentCacheData, responseData, otherArgs) {
        if (otherArgs.arg.skip > 0) {
          responseData.data.unshift(...currentCacheData.data);
          return responseData;
        }
        return responseData;
      },
    }),
    createSelection: builder.mutation<string, ISelectionCreate>({
      query: data => ({
        url: '',
        method: 'POST',
        data,
      }),
      invalidatesTags: ['Selections', 'SelectionsRefetch'],
    }),
    updateSelection: builder.mutation<string, ISelectionCreate>({
      query: data => ({
        url: ``,
        method: 'PUT',
        data,
      }),
      invalidatesTags: ['Selections', 'SelectionsRefetch'],
    }),
    deleteSelection: builder.mutation<string, number>({
      query: id => ({
        url: `/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Selections'],
    }),
    getSelectionById: builder.query<ISelectionCreate & { isEditable: boolean }, string>({
      query: (id: string) => ({
        url: `/${id}`,
        method: 'GET',
      }),
      keepUnusedDataFor: 0,
      transformResponse: (response: IAxiosResponse<ISelectionCreate & { isEditable: boolean }>) => ({
        ...response.data,
        criterias: getArrayCriterias(
          response.data.criterias as { rule: { rules: any[]; operationType: RuleOperationType } },
        ),
      }),
    }),
    copySelectionById: builder.query<ISelectionCreate & { masterId: number }, string>({
      query: (id: string) => ({
        url: `/${id}/for-copy`,
        method: 'GET',
      }),
      transformResponse: (response: IAxiosResponse<ISelectionCreate & { masterId: number }>) => ({
        ...response.data,
        criterias: getArrayCriterias(
          response.data.criterias as { rule: { rules: any[]; operationType: RuleOperationType } },
        ),
      }),
    }),
    getSelectionScopes: builder.query<ISelectionScope, string>({
      query: (id: string) => ({
        url: `/generation-scopes/${id}`,
        method: 'GET',
      }),
      keepUnusedDataFor: 0,
      transformResponse: (response: IAxiosResponse<ISelectionScope>) => response.data,
    }),
    refreshForGenerationInfo: builder.query<ISelectionItem[], number[]>({
      query: (ids: number[]) => ({
        url: '/for-refresh',
        method: 'POST',
        data: { selectionIds: ids },
      }),
      providesTags: ['SelectionsRefetch'],

      transformResponse: (response: IAxiosResponse<ISelectionItem[]>) => response.data,
    }),
    validateUniqueSelectionName: builder.query<boolean, string>({
      query: (selectionName: string) => ({
        url: `/valid-unique-name-for-user/${encodeURIComponent(selectionName)}`,
        method: 'POST',
      }),
    }),
    canCreateBulk: builder.query<boolean, unknown>({
      query: () => ({
        url: '/orders/can-create-bulk',
        method: 'GET',
      }),
      transformResponse: (response: IAxiosResponse<boolean>) => response.data,
    }),
    getPatients: builder.query<IPatientItem[], number>({
      query: id => ({
        url: `/${id}/orders/fn/patients`,
        method: 'GET',
      }),
      transformResponse: (response: IAxiosResponse<IPatientItem[]>) => response.data,
    }),
    getMissingOrderProducts: builder.query<
      IMissingOrderProducts[],
      { id: number; data: { laboratoryId: string; providerMaintenanceId: Nullable<string> } }
    >({
      query: ({ id, data }) => ({
        url: `/${id}/orders/fn/missed-orders-products`,
        method: 'POST',
        data,
      }),
      keepUnusedDataFor: 0,
      transformResponse: (response: IAxiosResponse<IMissingOrderProducts[]>) => response.data,
    }),
    getLabs: builder.query<ILabSearch[], unknown>({
      query: () => ({
        url: '/orders/laboratories',
        method: 'GET',
      }),
      transformResponse: (response: IAxiosResponse<ILabSearch[]>) => response.data,
    }),
    getOrderingProviders: builder.query<IOrderingProviderForOrder[], { laboratoryId: string; search: string }>({
      query: data => ({
        url: '/orders/fn/order-providers',
        method: 'POST',
        data,
      }),
      keepUnusedDataFor: 0,
      transformResponse: (response: IAxiosResponse<IOrderingProviderForOrder[]>) => response.data,
    }),
    getAdditionalOrdersProducts: builder.query<
      IProductSearch[],
      { laboratoryId: string; search: string; providerMaintenanceId: Nullable<string> }
    >({
      query: data => ({
        url: '/orders/fn/additional-orders-products',
        method: 'POST',
        data,
      }),
      keepUnusedDataFor: 0,
      transformResponse: (response: IAxiosResponse<IProductSearch[]>) => response.data,
    }),
    createBulkOrder: builder.query<unknown, { data: IBulkOrderBody; id: number }>({
      query: ({ data, id }) => ({
        url: `/${id}/bulk-orders`,
        method: 'POST',
        data,
      }),
      transformResponse: (response: IAxiosResponse<unknown>) => response.data,
    }),
  }),
});

const initialState: Nullable<ISelectionScope & { labsIsChange: boolean; visibilityIsChanged: boolean }> = {
  isAllLaboratories: false,
  isAllOrderingProviders: false,
  salesRepresentative: null,
  labsIsChange: false,
  visibilityIsChanged: false,
};

export const selectionScope = createSlice({
  name: 'scope',
  initialState,
  reducers: {
    setLaboratoriesScope: (state, action: PayloadAction<boolean>) => {
      state.isAllLaboratories = action.payload;
    },
    setProvidersScope: (state, action: PayloadAction<boolean>) => {
      state.isAllOrderingProviders = action.payload;
    },
    setAllScope: (state, action: PayloadAction<ISelectionScope>) => {
      state.isAllLaboratories = action.payload.isAllLaboratories;
      state.isAllOrderingProviders = action.payload.isAllOrderingProviders;
      state.salesRepresentative = action.payload.salesRepresentative;
    },
    setLabsIsChange: state => {
      state.labsIsChange = true;
    },
    setVisibilityIsChanged: state => {
      state.visibilityIsChanged = true;
    },
    setInitialScope: () => initialState,
  },
});

export const {
  setLaboratoriesScope,
  setProvidersScope,
  setAllScope,
  setLabsIsChange,
  setInitialScope,
  setVisibilityIsChanged,
} = selectionScope.actions;

export const selectedAddressReducer = selectionScope.reducer;
